import { Component, OnChanges, OnDestroy, Input, ViewChild, AfterViewInit, SimpleChanges } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { TeamsService } from '@Services';
import { BannerService } from '@Services/banner.service';
import { AppService } from '@App';
import { TeamWorkloadTableComponent } from '../team-workload-table/team-workload-table.component';
import { AccountWorkloadTableComponent } from '../account-workload-table/account-workload-table.component';
import { AssignedCasesTableComponent, AssignedCaseTableRow, AssignedCaseQueueItem } from '../assigned-cases-table/assigned-cases-table.component';
import { TeamPerformanceComponent } from '../team-performance/team-performance.component';
import { GetManageTeamsResponse } from '@App/manage-users/models/get-manage-teams-response.interface';
import { CycleTimeKPI, TeamPerformanceData, TeamUser } from '@App/models';
import { getSlaStatusFromDueDates } from '@Utils/sla-utils';
import { NewBusinessWorkbenchService } from '@App/new-business-workbench/services/new-business-workbench.service';
import { QueueFilterCarouselComponent, QueueFilterItem, DashboardSlaDueDateFilters, KeyPerformanceIndicators } from '../index';
import { CommonModule } from '@angular/common';
import { SharedModule } from '@Shared/shared.module';

const ALL_CASES_QUEUE_ID = 'ALL_CASES';

@Component({
    selector: 'team-dashboard-component',
    templateUrl: './team-dashboard.component.html',
    styleUrl: './team-dashboard.component.scss',
    imports: [CommonModule, SharedModule, TeamWorkloadTableComponent, AccountWorkloadTableComponent, AssignedCasesTableComponent, QueueFilterCarouselComponent, DashboardSlaDueDateFilters, KeyPerformanceIndicators, TeamPerformanceComponent],
    animations: [
        trigger('slideToggle', [
            transition(':enter', [
                style({ height: 0, opacity: 0, overflow: 'hidden' }),
                animate('250ms ease-out', style({ height: '*', opacity: 1 }))
            ]),
            transition(':leave', [
                style({ overflow: 'hidden' }),
                animate('200ms ease-in', style({ height: 0, opacity: 0 }))
            ])
        ])
    ]
})
export class TeamDashboardComponent implements OnChanges, AfterViewInit, OnDestroy {
    @Input() team: GetManageTeamsResponse;
    @Input() accountFilter: string[] = [];
    @Input() rawPerformanceData: TeamPerformanceData | null = null;
    @Input() rawTeamDashboardData: any[] | null = null;
    @Input() teamTasksEnabled: boolean = false;
    @ViewChild(AssignedCasesTableComponent) assignedCasesTable: AssignedCasesTableComponent;
    @ViewChild(TeamWorkloadTableComponent) teamWorkloadTable: TeamWorkloadTableComponent;
    @ViewChild(AccountWorkloadTableComponent) accountWorkloadTable: AccountWorkloadTableComponent;

    teamData: TeamUser[] = [];
    teamMemberSchedules = [];
    assignedCases: AssignedCaseTableRow[] = [];
    assignedTasks: any[] = [];
    teamUsers: { id: string; userName: string }[] = [];
    userCaseCounts: Record<string, number> = {};
    isQueueOrSlaFilterActive: boolean = false;
    selectedTeamMemberIds: string[] = [];
    selectedAccountIds: string[] = [];
    selectedQueueIds: string[] = [];
    queueFilters: QueueFilterItem[] = [];
    allCasesQueueId = ALL_CASES_QUEUE_ID;
    isSideNavHidden = false;
    isFilterPaneCollapsed = false;
    filteredAssignedCases: AssignedCaseTableRow[] = [];
    memberFilteredAssignedCases: AssignedCaseTableRow[] = [];
    accountScopedCases: AssignedCaseTableRow[] = [];
    accountScopedTasks: any[] = [];
    filteredAssignedTasks: any[] = [];
    uniqueQueueIds: string[] = [];
    queues: { id: string; name?: string; order?: number; isServiceLevelAgreementEnabled?: boolean; serviceLevelAgreement?: { hours: number } }[] = [];

    // KPI Data
    kpiData: CycleTimeKPI[] = [];
    filteredKpiData: CycleTimeKPI[] = [];

    slaFilters: any[] = [];
    slaDueDateCounts: any = {
        pastDue: 0,
        dueToday: 0,
        dueTomorrow: 0
    };
    isSLAFilterApplied: boolean = false;
    atLeastOneQueueHasSLA: boolean = false;
    private queuesLoaded: boolean = false;

    constructor(
        private readonly teamsService: TeamsService,
        private readonly newBusinessWorkbenchService: NewBusinessWorkbenchService,
        private readonly bannerService: BannerService,
        private readonly appService: AppService
    ) {

    }

    ngOnChanges(changes?: SimpleChanges): void {
        if (!this.team) {
            return;
        }
        if (!changes || changes['team']) {
            this.clearCaseReportingFilters();
            this.queuesLoaded = false;
            this.subscribeTeamMemberSchedules();
            this.subscribeKpiData();
            return;
        }
        if (changes['rawTeamDashboardData']) {
            if (this.queuesLoaded) {
                this.processRawTeamData();
            }
            return;
        }
        if (changes['accountFilter']) {
            const accountFilterChanged = !changes['accountFilter'].firstChange
                && JSON.stringify(changes['accountFilter'].previousValue) !== JSON.stringify(changes['accountFilter'].currentValue);

            if (accountFilterChanged) {
                this.clearCaseReportingFilters();
            }
            this.applyAllFilters();
        }
    }

    ngAfterViewInit() {
        const sidenav = document.getElementById('sidenav');
        if (sidenav) {
            const observer = new MutationObserver(mutations => {
                mutations.forEach(mutation => {
                    if (mutation.attributeName === 'class') {
                        this.isSideNavHidden = sidenav.classList.contains('hidenav');
                    }
                });
            });
            observer.observe(sidenav, {
                attributes: true,
                attributeFilter: ['class']
            });
        }
    }

    loadTeamData(): void {
        this.newBusinessWorkbenchService.getAllWorkbenchQueues(this.team.clientId).subscribe(queues => {
            this.queues = queues || [];
            this.queuesLoaded = true;

            this.atLeastOneQueueHasSLA = this.queues.some(q => q.isServiceLevelAgreementEnabled && q.serviceLevelAgreement?.hours);
            if (!this.atLeastOneQueueHasSLA) {
                const canConfigure = this.appService.checkACL('Case Manager: Configure', 'W');
                this.bannerService.showBanner({
                    title: 'Unlock SLA tracking for your queues.',
                    message: 'Set response and resolution targets so your team can see what\'s breached, what\'s at risk, and where to focus first.',
                    dismissKey: 'no-sla-configured-team',
                    actions: canConfigure
                        ? [{ label: 'CONFIGURE SLAS', route: '/casemanager/edit', variant: 'primary' }]
                        : []
                });
            } else {
                this.bannerService.clearDismissal('no-sla-configured-team');
            }

            this.processRawTeamData();
        });
    }

    ngOnDestroy(): void {
        this.bannerService.clearBanner();
    }

    processRawTeamData(): void {
        if (!this.rawTeamDashboardData) {
            return;
        }
        const data = this.rawTeamDashboardData;
        data.forEach((user: any) => {
                user.pastDue = 0;
                user.dueToday = 0;
                user.dueTomorrow = 0;

                const dueDatesByCase = this.getQueueDueDatesByCaseId(user.casesAssigned);
                Object.values(dueDatesByCase).forEach((slaDueDates: string[]) => {
                    const slaStatuses = getSlaStatusFromDueDates(slaDueDates);
                    if (slaStatuses.includes('pastDue')) user.pastDue += 1;
                    if (slaStatuses.includes('dueToday')) user.dueToday += 1;
                    if (slaStatuses.includes('dueTomorrow')) user.dueTomorrow += 1;
                });

                const userSchedule = this.teamMemberSchedules.find((s: any) => s.userId === user.id);
                user.availableStatus = this.getUserAvailabilityClass(userSchedule);
            });
        this.teamData = data;

        this.assignedCases = data.flatMap((user: any) =>
            (user.casesAssigned || []).map((caseItem: any) => ({
                account: caseItem.accountName || '',
                accountId: caseItem.accountId || '',
                parentAccountId: caseItem.parentAccountId || '',
                caseDisplayNumber: caseItem.caseNumber || '',
                policyNumber: caseItem.policyNumber || '',
                assignedTo: user.userName || '',
                assignedToUserId: user.id || '',
                faceAmount: caseItem.faceAmount || '',
                queue: this.mapQueueItemsWithSlaHours(caseItem.caseQueueSlaDueDates || []),
                requirementsReceived: caseItem.requirementsReceived || [],
                caseId: caseItem.caseId || '',
                openTasks: (caseItem.openTasks || []).map((t: any) => ({ name: t.name || '' }))
            }))
        );
        this.assignedTasks = data.flatMap((user: any) =>
            (user.tasksAssigned || []).map((task: any) => ({
                account: task.accountName || '',
                accountId: task.accountId || '',
                parentAccountId: task.parentAccountId || '',
                caseId: task.caseId || '',
                followUpDate: task.followUpDate || '',
                assignedToUserId: user.id || ''
            }))
        );
        this.teamUsers = data.map((u: any) => ({ id: u.id, userName: u.userName || '' }));
        this.userCaseCounts = data.reduce((acc: Record<string, number>, u: any) => {
            acc[u.id] = (u.casesAssigned || []).length;
            return acc;
        }, {});
        this.extractUniqueCaseQueueIds(this.assignedCases);
        this.applyAllFilters();
    }

    subscribeTeamMemberSchedules(): void {
        this.teamsService.getTeamMemberSchedules(this.team.id).subscribe(schedules => {
            this.teamMemberSchedules = schedules;
            this.loadTeamData();
        });
    }

    subscribeKpiData(): void {
        this.teamsService.getCycleTimeKPI(this.team.clientId, this.team.id).subscribe((kpiData: any) => {
            this.kpiData = kpiData || [];
            this.filterKpiData();
        });
    }

    applyAllFilters(): void {
        this.accountScopedCases = this.scopeByAccount(this.assignedCases || []);
        this.accountScopedTasks = this.scopeTasksByAccount(this.assignedTasks || []);

        let memberScoped = this.accountScopedCases;

        if (this.selectedTeamMemberIds.length > 0) {
            memberScoped = memberScoped.filter(row => this.selectedTeamMemberIds.includes(row.assignedToUserId));
        } else if (this.selectedAccountIds.length > 0) {
            memberScoped = memberScoped.filter(row => this.selectedAccountIds.includes(row.parentAccountId));
        }

        const slaFilter = (cases: AssignedCaseTableRow[]) =>
            cases.filter(row => {
                const dueDates = (row.queue || []).map(q => q.dueDate).filter(d => !!d);
                const slaStatuses = getSlaStatusFromDueDates(dueDates);
                return this.slaFilters.some(f => slaStatuses.includes(f));
            });

        const queueFilter = (cases: AssignedCaseTableRow[]) =>
            cases.filter(row =>
                (row.queue || []).some(q => q.queueId && this.selectedQueueIds.includes(q.queueId))
            );

        let slaFiltered = this.slaFilters.length > 0 ? slaFilter(memberScoped) : memberScoped;
        let queueFiltered = this.selectedQueueIds.length > 0 ? queueFilter(memberScoped) : memberScoped;

        let bothFiltered = memberScoped;
        if (this.slaFilters.length > 0 && this.selectedQueueIds.length > 0) {
            bothFiltered = queueFilter(slaFiltered);
        } else if (this.slaFilters.length > 0) {
            bothFiltered = slaFiltered;
        } else if (this.selectedQueueIds.length > 0) {
            bothFiltered = queueFiltered;
        }

        this.generateQueueFilters(slaFiltered);
        this.updateSlaDueDateCounts(queueFiltered);

        const isFiltered = this.selectedQueueIds.length > 0 || this.slaFilters.length > 0 || this.isAccountFilterActive();
        this.isQueueOrSlaFilterActive = isFiltered;

        this.filteredAssignedCases = bothFiltered;
        this.memberFilteredAssignedCases = bothFiltered;

        let taskScoped = this.accountScopedTasks;
        if (this.selectedTeamMemberIds.length > 0) {
            taskScoped = taskScoped.filter(t => this.selectedTeamMemberIds.includes(t.assignedToUserId));
        } else if (this.selectedAccountIds.length > 0) {
            taskScoped = taskScoped.filter(t => this.selectedAccountIds.includes(t.parentAccountId));
        }
        this.filteredAssignedTasks = taskScoped;

        this.filterKpiData();
    }

    isAccountFilterActive(): boolean {
        return this.accountFilter?.length > 0;
    }

    scopeByAccount(cases: AssignedCaseTableRow[]): AssignedCaseTableRow[] {
        if (!this.isAccountFilterActive()) {
            return cases;
        }

        return cases.filter(row =>
            this.accountFilter.some(id => (row.parentAccountId || row.accountId || '').toLowerCase() === id.toLowerCase())
        );
    }

    scopeTasksByAccount(tasks: any[]): any[] {
        if (!this.isAccountFilterActive()) {
            return tasks;
        }

        return tasks.filter(task =>
            this.accountFilter.some(id => (task.parentAccountId || task.accountId || '').toLowerCase() === id.toLowerCase())
        );
    }

    filterKpiData(): void {
        if (!this.isAccountFilterActive()) {
            this.filteredKpiData = this.kpiData;
            return;
        }

        this.filteredKpiData = this.kpiData.filter(kpi =>
            this.accountFilter.some(id => (kpi.parentAccountId || '').toLowerCase() === id.toLowerCase())
        );
    }

    getQueueName(queueId: string): string | undefined {
        const q = this.queues.find(x => x.id === queueId);
        return q?.name;
    }

    extractUniqueCaseQueueIds(cases: AssignedCaseTableRow[]): void {
        const ids = new Set<string>();

        for (const c of cases) {
            for (const item of c.queue ?? []) {
                const matchingQueue = this.queues.find(q => q.id === item.queueId);
                if (!matchingQueue) {
                    continue;
                }
                ids.add(item.queueId);
            }
        }

        this.uniqueQueueIds = Array.from(ids);
    }

    generateQueueFilters(casesForCounts: AssignedCaseTableRow[]): void {

        const queueCounts = new Map<string, number>();
        for (const id of this.uniqueQueueIds) {
            queueCounts.set(id, 0);
        }
        for (const row of casesForCounts) {
            for (const item of row.queue || []) {
                if (item.queueId && queueCounts.has(item.queueId)) {
                    queueCounts.set(item.queueId, (queueCounts.get(item.queueId) ?? 0) + 1);
                }
            }
        }
        const filters: QueueFilterItem[] = [{
            id: this.allCasesQueueId,
            name: 'All Cases',
            count: casesForCounts.length,
            checked: this.selectedQueueIds.length === 0
        }];
        const sortedQueues = this.uniqueQueueIds
            .map(id => this.queues.find(q => q.id === id))
            .filter(q => !!q)
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        for (const q of sortedQueues) {
            const count = queueCounts.get(q.id) ?? 0;
            filters.push({
                id: q.id,
                name: q.name ?? q.id,
                count,
                checked: this.selectedQueueIds.includes(q.id)
            });
        }
        this.queueFilters = filters;
    }

    onQueueFilterEmitted(value: string): void {
        if (value === this.allCasesQueueId) {
            this.selectedQueueIds = [];
        } else {
            const idx = this.selectedQueueIds.indexOf(value);
            if (idx === -1) {
                this.selectedQueueIds = [...this.selectedQueueIds, value];
            } else {
                this.selectedQueueIds = this.selectedQueueIds.filter(id => id !== value);
            }
        }
        this.updateQueueFiltersCheckedState();
        this.applyAllFilters();
        if (this.assignedCasesTable) {
            this.assignedCasesTable.clearSelection();
        }
    }

    getSelectedFiltersCount(): number {
        return this.selectedQueueIds.length;
    }

    updateQueueFiltersCheckedState(): void {
        this.queueFilters.forEach(f => {
            f.checked = f.id === this.allCasesQueueId
                ? this.selectedQueueIds.length === 0
                : this.selectedQueueIds.includes(f.id);
        });
    }

    mapQueueItemsWithSlaHours(items: any[]): AssignedCaseQueueItem[] {
        return items.map(q => {
            const queueId = q.queueId;
            const queue = this.queues.find(qu => qu.id === queueId);
            q.slaHours = queue?.serviceLevelAgreement?.hours;
            q.isSlaEnabled = queue?.isServiceLevelAgreementEnabled;
            return q;
        });
    }

    onTeamMembersSelectionChange(selected: any[]): void {
        const newIds = (selected || []).map((u: any) => u.id);
        if (newIds.length === this.selectedTeamMemberIds.length &&
            newIds.every(id => this.selectedTeamMemberIds.includes(id))) {
            return;
        }
        this.selectedTeamMemberIds = newIds;
        this.applyAllFilters();
        if (this.assignedCasesTable) {
            this.assignedCasesTable.clearSelection();
        }
    }

    onAccountsSelectionChange(selected: any[]): void {
        const newIds = (selected || []).map((a: any) => a.parentAccountId);
        if (newIds.length === this.selectedAccountIds.length &&
            newIds.every(id => this.selectedAccountIds.includes(id))) {
            return;
        }
        this.selectedAccountIds = newIds;
        this.applyAllFilters();
        if (this.assignedCasesTable) {
            this.assignedCasesTable.clearSelection();
        }
    }

    onTabChange(tabIndex: number): void {
        if (tabIndex === 0) {
            // Switching to By Cases By Member
            this.selectedAccountIds = [];
            if (this.accountWorkloadTable) {
                this.accountWorkloadTable.selection.clear();
            }
        } else {
            // Switching to Cases By Account
            this.selectedTeamMemberIds = [];
            if (this.teamWorkloadTable) {
                this.teamWorkloadTable.selection.clear();
            }
        }
        this.applyAllFilters();
        if (this.assignedCasesTable) {
            this.assignedCasesTable.clearSelection();
        }
    }

    updateSlaDueDateCounts(cases: AssignedCaseTableRow[]): void {
        let pastDue = 0;
        let dueToday = 0;
        let dueTomorrow = 0;

        for (const row of cases) {
            const dates = (row.queue || []).map(q => q.dueDate).filter(d => !!d);
            if (dates.length === 0) continue;

            const slaStatuses = getSlaStatusFromDueDates(dates);
            if (slaStatuses.includes('pastDue')) pastDue += 1;
            if (slaStatuses.includes('dueToday')) dueToday += 1;
            if (slaStatuses.includes('dueTomorrow')) dueTomorrow += 1;
        }

        this.slaDueDateCounts = { pastDue, dueToday, dueTomorrow };
    }

    getQueueDueDatesByCaseId(casesAssigned: any[]) {
        const map: Record<string, string[]> = {};

        casesAssigned.forEach(c => {
            if (!c.caseId) return;

            map[c.caseId] ??= [];

            c.caseQueueSlaDueDates.forEach(q => {
                map[c.caseId].push(q.dueDate);
            });
        });

        return map;
    }

    isScheduledUnavailableNow(schedules: any[]): boolean {
        if (!schedules || schedules.length === 0) return false;
        const now = new Date();
        for (const schedule of schedules) {
            const start = new Date(schedule.formattedStartTime);
            const end = new Date(schedule.formattedEndTime);
            if (now >= start && now <= end)
                return true;
        }
        return false;
    }

    getUserAvailabilityClass(row: any): string {
        if (row?.userAvailable) {
            return 'available-state';
        }
        else if (this.isScheduledUnavailableNow(row?.schedules)) {
            return 'scheduled-off-state';
        }
        else {
            return 'toggled-off-state';
        }
    }

    handleslaFiltersEmitted(value: string) {
        this.slaFilters = value.split(',');
        if(this.slaFilters.length === 1 && this.slaFilters[0] === '') {
            this.slaFilters = [];
        }
        this.isSLAFilterApplied = this.slaFilters.length > 0;
        this.applyAllFilters();
    }

    private clearCaseReportingFilters(): void {
        this.selectedQueueIds = [];
        this.slaFilters = [];
        this.isSLAFilterApplied = false;
        this.selectedTeamMemberIds = [];
        this.selectedAccountIds = [];

        if (this.teamWorkloadTable) {
            this.teamWorkloadTable.selection.clear();
        }

        if (this.accountWorkloadTable) {
            this.accountWorkloadTable.selection.clear();
        }

        if (this.assignedCasesTable) {
            this.assignedCasesTable.clearSelection();
        }
    }
}